import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Plane, Shield, Building2, Calendar, MapPin, User, FileText, PenLine } from "lucide-react";
import { API } from "@/lib/api";

/**
 * Halaman public (tanpa auth) — menampilkan verifikasi dokumen SPPD/ST
 * saat user men-scan QR code dari dokumen PDF. URL: /verify/sppd/{nomor_encoded}
 * Query opsional: ?signer=<key>&phase=<awal|akhir|terbit>
 */
export default function VerifySppd() {
  const { nomor } = useParams();
  const [params] = useSearchParams();
  const signer = params.get("signer") || "";
  const phase = params.get("phase") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const q = new URLSearchParams();
        if (signer) q.set("signer", signer);
        if (phase) q.set("phase", phase);
        const qs = q.toString();
        const res = await fetch(`${API}/sppd/verify/${nomor}${qs ? `?${qs}` : ""}`);
        if (!res.ok) throw new Error("Dokumen tidak ditemukan");
        setData(await res.json());
      } catch (e) {
        setError(e.message || "Gagal memverifikasi dokumen");
      } finally {
        setLoading(false);
      }
    })();
  }, [nomor, signer, phase]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl" data-testid="verify-sppd-page">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-12 h-12 rounded-full bg-sky-600 flex items-center justify-center text-white shadow-lg mb-3">
            <Plane size={22} />
          </div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Verifikasi Dokumen SPPD</h1>
          <p className="text-sm text-slate-600 mt-1">Sistem ATLAS — PT Sucofindo EPISI</p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-400">Memverifikasi dokumen…</div>
          ) : error || !data?.valid ? (
            <div className="p-10 text-center" data-testid="verify-invalid">
              <div className="w-16 h-16 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto mb-4">
                <XCircle size={32} />
              </div>
              <div className="text-xl font-extrabold text-slate-900">Dokumen Tidak Valid</div>
              <div className="text-sm text-slate-600 mt-2">
                {data?.message || error || "Nomor dokumen tidak ditemukan dalam sistem."}
              </div>
              <div className="text-xs text-slate-400 mt-4 font-mono">{decodeURIComponent(nomor || "")}</div>
            </div>
          ) : (
            <>
              <div className="bg-emerald-50 border-b border-emerald-200 p-6" data-testid="verify-valid">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-lg">
                    <CheckCircle2 size={28} />
                  </div>
                  <div>
                    <div className="font-display text-xl font-extrabold text-emerald-900">Dokumen Valid</div>
                    <div className="text-xs text-emerald-800 mt-0.5">{data.message}</div>
                  </div>
                </div>
              </div>

              {/* Signer-specific banner (jika QR berasal dari slot TTD tertentu) */}
              {data.signer_info && (
                <div className="bg-indigo-50 border-b border-indigo-200 p-5" data-testid="verify-signer-banner">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-xl bg-white border border-indigo-200 text-indigo-700 flex items-center justify-center shrink-0 shadow-sm">
                      <PenLine size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-indigo-700">
                        Konteks Tanda Tangan
                      </div>
                      <div className="text-base font-extrabold text-indigo-900 mt-0.5">
                        {data.signer_info.label}
                        {data.signer_info.phase_label ? ` — ${data.signer_info.phase_label.split(' (')[0]}` : ""}
                      </div>
                      <div className="text-sm text-indigo-800 mt-1">
                        Penandatangan: <b>{data.signer_info.name}</b>
                      </div>
                      {data.signer_info.phase_date && (
                        <div className="text-xs text-indigo-700 mt-0.5">
                          Tanggal: {formatDate(data.signer_info.phase_date)}
                        </div>
                      )}
                      {data.signer_info.phase_label && (
                        <div className="text-[11px] text-indigo-600 mt-1 italic">
                          {data.signer_info.phase_label}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field icon={FileText} label="Nomor Surat Tugas" value={data.nomor_st} mono />
                  <Field icon={FileText} label="Nomor SPPD" value={data.nomor_sppd} mono />
                  <Field icon={User} label="Pelaksana" value={`${data.pegawai_nama || ""} (${data.pegawai_npp || "—"})`} />
                  <Field icon={Shield} label="Jabatan" value={data.pegawai_jabatan} />
                  <Field icon={Building2} label="Perusahaan Tujuan" value={data.nama_perusahaan} />
                  <Field icon={MapPin} label="Tempat Pelaksanaan" value={data.tempat_pelaksanaan} />
                  <Field icon={Calendar} label="Tanggal Berangkat" value={formatDate(data.awal_penugasan)} />
                  <Field icon={Calendar} label="Tanggal Kembali" value={formatDate(data.akhir_penugasan)} />
                  <div className="md:col-span-2">
                    <Field icon={FileText} label="Keperluan / Pekerjaan" value={data.untuk_melaksanakan} />
                  </div>
                  <div className="md:col-span-2">
                    <Field icon={Shield} label="Diterbitkan Oleh" value={`${data.penandatangan_st_nama || "—"} — ${data.penandatangan_st_jabatan || ""}`} />
                  </div>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700">
                  <div>Status: <b>{data.status}</b></div>
                  <div>Tgl. Terbit ST: {formatDate(data.tanggal_terbit_st)}</div>
                  <div>Tgl. SPPD: {formatDate(data.tanggal_sppd)}</div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="text-center mt-6 text-xs text-slate-500">
          © 2026 PT Sucofindo EPISI — Sistem ATLAS ·{" "}
          <Link to="/" className="text-sky-700 hover:underline">Masuk ke ATLAS</Link>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-8 h-8 rounded-lg bg-sky-50 border border-sky-200 flex items-center justify-center text-sky-700 shrink-0 mt-0.5">
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
        <div className={`text-sm text-slate-900 font-medium mt-0.5 ${mono ? "font-mono-clock" : ""}`}>
          {value || <span className="text-slate-300">—</span>}
        </div>
      </div>
    </div>
  );
}

const BULAN_ID = ["", "Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

function formatDate(iso) {
  if (!iso) return null;
  try {
    const [y, m, d] = String(iso).split("-");
    return `${parseInt(d, 10)} ${BULAN_ID[parseInt(m, 10)]} ${y}`;
  } catch (e) {
    return iso;
  }
}
